import { IAccountActions } from "@/account/account-actions";
import type { IUserInfo } from "@/account/account-models";
import type { IChatInfo } from "@/chats/chats-models";
import { IMessengerActions } from "@/chats/messenger/messenger-actions";
import { IHeaderModalActions } from "@/common/header-modal/header-modal-actions";
import { IFeedActions } from "@/feed/feed-actions";
import type { IGetFeedConfig } from "@/feed/feed-helpers";
import type { IFeedInfo } from "@/feed/feed-models";
import { ILandingActions } from "@/landing/landing-actions";
import { createReducer } from "@reduxjs/toolkit";
import { IModelProfileActions } from "./model-profile-actions";

export interface IModelProfileModel {
  profileUser: IUserInfo | null;
  profileChat: IChatInfo | null;

  nextProfileFeedUrl: string | null;
  profileFeed: IFeedInfo[] | null;

  nextProfileFeedPhotosUrl: string | null;
  profileFeedPhotos: IFeedInfo[] | null;

  nextProfileFeedVideosUrl: string | null;
  profileFeedVideos: IFeedInfo[] | null;
}

const INITIAL_STATE: IModelProfileModel = {
  profileUser: null,
  profileChat: null,

  nextProfileFeedUrl: null,
  profileFeed: null,

  nextProfileFeedPhotosUrl: null,
  profileFeedPhotos: null,

  nextProfileFeedVideosUrl: null,
  profileFeedVideos: null,
};

/** Reducer */
export const modelProfileReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(
      IModelProfileActions.initGetProfileUser,
      (state, action): IModelProfileModel => {
        const cachedUser = action.payload as IUserInfo;
        return {
          ...state,
          profileUser: cachedUser || null,
        };
      }
    )
    .addCase(
      IModelProfileActions.successGetProfileUser,
      (state, action): IModelProfileModel => {
        return {
          ...state,
          profileUser: action.payload,
        };
      }
    )
    .addCase(
      IModelProfileActions.failureGetProfileUser,
      (state, action): IModelProfileModel => {
        return {
          ...state,
          profileUser: null,
        };
      }
    )
    .addCase(
      IModelProfileActions.resetGetProfileUser,
      (state, action): IModelProfileModel => {
        return {
          ...state,
          profileUser: null,
        };
      }
    )
    .addCase(
      IModelProfileActions.initGetProfileFeed,
      (state, action): IModelProfileModel => {
        const config: IGetFeedConfig = action.payload.config;

        // prevent issue when feed request is delayed from other model
        // and then loads and overwrites the current model feed
        if (config.modelId !== state.profileUser?._id) return state;

        const nextState = {
          profileFeed: config.isInitialRequest ? null : state.profileFeed,
        };
        return {
          ...state,
          ...nextState,
        };
      }
    )
    .addCase(
      IModelProfileActions.successGetProfileFeed,
      (state, action): IModelProfileModel => {
        const config: IGetFeedConfig = action.payload.config;

        // prevent issue when feed request is delayed from other model
        // and then loads and overwrites the current model feed
        if (config.modelId !== state.profileUser?._id) return state;

        const res = action.payload.res;

        let profileFeed: IFeedInfo[] = res.data;
        const nextProfileFeedUrl = res.data.length > 0 ? res.next || "" : "";

        if (!config.isInitialRequest) {
          profileFeed = [...(state.profileFeed || []), ...res.data];
        }

        return {
          ...state,
          nextProfileFeedUrl,
          profileFeed,
        };
      }
    )
    .addCase(
      IModelProfileActions.initGetProfileFeedPhotos,
      (state, action): IModelProfileModel => {
        const { isInitialRequest } = action.payload.config;
        const nextState = {
          profileFeedPhotos: isInitialRequest ? null : state.profileFeedPhotos,
        };
        return {
          ...state,
          ...nextState,
        };
      }
    )
    .addCase(
      IModelProfileActions.successGetProfileFeedPhotos,
      (state, action): IModelProfileModel => {
        const config: IGetFeedConfig = action.payload.config;
        const res = action.payload.res;

        let profileFeedPhotos: IFeedInfo[] = res.data;
        const nextProfileFeedPhotosUrl =
          res.data.length > 0 ? res.next || "" : "";

        if (!config.isInitialRequest) {
          profileFeedPhotos = [...(state.profileFeedPhotos || []), ...res.data];
        }

        return {
          ...state,
          nextProfileFeedPhotosUrl,
          profileFeedPhotos,
        };
      }
    )
    .addCase(
      IModelProfileActions.initGetProfileFeedVideos,
      (state, action): IModelProfileModel => {
        const { isInitialRequest } = action.payload.config;
        const nextState = {
          profileFeedVideos: isInitialRequest ? null : state.profileFeedVideos,
        };
        return {
          ...state,
          ...nextState,
        };
      }
    )
    .addCase(
      IModelProfileActions.successGetProfileFeedVideos,
      (state, action): IModelProfileModel => {
        const config: IGetFeedConfig = action.payload.config;
        const res = action.payload.res;

        let profileFeedVideos: IFeedInfo[] = res.data;
        const nextProfileFeedVideosUrl =
          res.data.length > 0 ? res.next || "" : "";

        if (!config.isInitialRequest) {
          profileFeedVideos = [...(state.profileFeedVideos || []), ...res.data];
        }

        return {
          ...state,
          nextProfileFeedVideosUrl,
          profileFeedVideos,
        };
      }
    )
    // A post edited or removed from the profile's viewer must change in the
    // profile's own lists too (separate copies from the feed's).
    .addCase(
      IFeedActions.feedItemPatched,
      (state, action): IModelProfileModel => {
        const { data } = action.payload;
        const replace = (list: IFeedInfo[] | null) =>
          list && list.map((item) => (item._id === data._id ? data : item));
        return {
          ...state,
          profileFeed: replace(state.profileFeed),
          profileFeedPhotos: replace(state.profileFeedPhotos),
          profileFeedVideos: replace(state.profileFeedVideos),
        };
      }
    )
    .addCase(
      IHeaderModalActions.feedItemDeleted,
      (state, action): IModelProfileModel => {
        const drop = (list: IFeedInfo[] | null) =>
          list && list.filter((item) => item._id !== action.payload.id);
        return {
          ...state,
          profileFeed: drop(state.profileFeed),
          profileFeedPhotos: drop(state.profileFeedPhotos),
          profileFeedVideos: drop(state.profileFeedVideos),
        };
      }
    )
    // Photos managed from the profile's own viewer: the profile page keeps its
    // own copy of the user, separate from the logged-in user's.
    .addCase(
      ILandingActions.imageDeleted,
      (state, action): IModelProfileModel => {
        const { public_id, media_id } = action.payload;
        if (!state.profileUser?.featured) {
          return state;
        }
        return {
          ...state,
          profileUser: {
            ...state.profileUser,
            featured: state.profileUser.featured.filter(
              (img) =>
                !(
                  (public_id && img.public_id === public_id) ||
                  (media_id && img.media_id === media_id)
                )
            ),
          },
        };
      }
    )
    .addCase(
      IAccountActions.successGetUser,
      (state, action): IModelProfileModel => {
        // The logged-in user was reloaded (e.g. after "Set as profile photo"):
        // when that is the profile on screen, show the new photos too.
        if (state.profileUser?._id !== action.payload._id) {
          return state;
        }
        return {
          ...state,
          profileUser: {
            ...state.profileUser,
            avatar: action.payload.avatar,
            featured: action.payload.featured,
            greeting: action.payload.greeting,
          },
        };
      }
    )
    .addCase(
      IModelProfileActions.resetGetProfileFeed,
      (state, action): IModelProfileModel => {
        const nextState = {
          profileFeed: null,
          profileFeedPhotos: null,
          profileFeedVideos: null,
        };
        return {
          ...state,
          ...nextState,
        };
      }
    )
    .addCase(
      IModelProfileActions.initGetProfileChat,
      (state, action): IModelProfileModel => {
        const cachedChat = action.payload.cachedChat as IChatInfo;
        return {
          ...state,
          profileChat: cachedChat || null,
        };
      }
    )
    .addCase(
      IModelProfileActions.successGetProfileChat,
      (state, action): IModelProfileModel => {
        return {
          ...state,
          profileChat: action.payload,
        };
      }
    )
    .addCase(
      IModelProfileActions.resetGetProfileChat,
      (state, action): IModelProfileModel => {
        return {
          ...state,
          profileChat: null,
        };
      }
    )
    .addCase(IMessengerActions.updateChatById, (state, action) => {
      if (
        !state.profileChat ||
        state.profileChat._id !== action.payload.chat_id
      ) {
        return state;
      }
      return {
        ...state,
        profileChat: {
          ...state.profileChat,
          ...action.payload.newValues,
        },
      };
    })
    .addCase(
      IModelProfileActions.setProfileUserFollowed,
      (state, action): IModelProfileModel => {
        return {
          ...state,
          profileUser: {
            ...state.profileUser!,
            is_followee: action.payload,
          },
        };
      }
    );
});
